/**
 * urlDiff.js — pure diff for two ad-request URLs (GAM ad calls, Prebid
 * endpoints, etc). Decodes query params, sorts them changed/added/removed
 * first then alphabetical (same last), and sub-expands any form-encoded
 * param value (e.g. GAM `cust_params`) into a nested per-key diff.
 *
 * Never throws — an unparseable URL yields `{ ok: false, reason }`.
 */

function safeParseUrl(u) {
  try {
    return new URL(String(u));
  } catch {
    return null;
  }
}

function decodeComponent(s) {
  try {
    return decodeURIComponent(s.replace(/\+/g, ' '));
  } catch {
    return s;
  }
}

/** Parse a query-string-like blob ("a=1&b=2") into an ordered array of
 *  [key, value] pairs, decoding both. Never throws. */
function parsePairs(qs) {
  const pairs = [];
  if (!qs) return pairs;
  const raw = qs.startsWith('?') ? qs.slice(1) : qs;
  if (!raw) return pairs;
  for (const part of raw.split('&')) {
    if (part === '') continue;
    const eq = part.indexOf('=');
    let k, v;
    if (eq === -1) { k = part; v = ''; }
    else { k = part.slice(0, eq); v = part.slice(eq + 1); }
    pairs.push([decodeComponent(k), decodeComponent(v)]);
  }
  return pairs;
}

/** Group ordered [key,value] pairs into a Map<key, joinedValue> — duplicate
 *  keys are joined with ' | ' in encounter order. */
function groupPairs(pairs) {
  const map = new Map();
  for (const [k, v] of pairs) {
    if (map.has(k)) map.set(k, `${map.get(k)} | ${v}`);
    else map.set(k, v);
  }
  return map;
}

function looksFormEncoded(v) {
  return typeof v === 'string' && v.indexOf('=') !== -1 && v.indexOf('&') !== -1;
}

function sortEntries(entries) {
  const rank = { changed: 0, added: 1, removed: 2, same: 3 };
  return entries.slice().sort((a, b) => {
    const ra = rank[a.kind] ?? 1;
    const rb = rank[b.kind] ?? 1;
    if (ra !== rb) return ra - rb;
    if (a.key < b.key) return -1;
    if (a.key > b.key) return 1;
    return 0;
  });
}

/** Diff two Map<key,value> param sets into the entry[] shape. */
function diffParamMaps(leftMap, rightMap) {
  const keys = new Set([...leftMap.keys(), ...rightMap.keys()]);
  const entries = [];
  for (const key of keys) {
    const hasL = leftMap.has(key);
    const hasR = rightMap.has(key);
    if (hasL && !hasR) entries.push({ key, kind: 'removed', left: leftMap.get(key), right: undefined });
    else if (!hasL && hasR) entries.push({ key, kind: 'added', left: undefined, right: rightMap.get(key) });
    else {
      const l = leftMap.get(key);
      const r = rightMap.get(key);
      entries.push({ key, kind: l === r ? 'same' : 'changed', left: l, right: r });
    }
  }
  return sortEntries(entries);
}

export function urlDiff(leftUrl, rightUrl) {
  const l = safeParseUrl(leftUrl);
  const r = safeParseUrl(rightUrl);

  if (!l || !r) {
    return {
      ok: false,
      reason: !l && !r ? 'both URLs failed to parse' : (!l ? 'left URL failed to parse' : 'right URL failed to parse'),
      same: false,
      origin: { left: leftUrl != null ? String(leftUrl) : leftUrl, right: rightUrl != null ? String(rightUrl) : rightUrl, same: false },
      path: { left: undefined, right: undefined, same: false },
      params: [],
      nested: {},
    };
  }

  try {
    const leftOrigin = l.protocol + '//' + l.host;
    const rightOrigin = r.protocol + '//' + r.host;
    const originSame = leftOrigin === rightOrigin;

    const pathSame = l.pathname === r.pathname;

    const leftPairs = parsePairs(l.search);
    const rightPairs = parsePairs(r.search);
    const leftMap = groupPairs(leftPairs);
    const rightMap = groupPairs(rightPairs);

    const params = diffParamMaps(leftMap, rightMap);

    const nested = {};
    for (const key of new Set([...leftMap.keys(), ...rightMap.keys()])) {
      if (!leftMap.has(key) || !rightMap.has(key)) continue;
      const lv = leftMap.get(key);
      const rv = rightMap.get(key);
      if (!looksFormEncoded(lv) || !looksFormEncoded(rv)) continue;
      const subLeft = groupPairs(parsePairs(lv));
      const subRight = groupPairs(parsePairs(rv));
      const subEntries = diffParamMaps(subLeft, subRight);
      if (subEntries.some((e) => e.kind !== 'same')) {
        nested[key] = subEntries;
      }
    }

    const allParamsSame = params.every((e) => e.kind === 'same');
    const same = originSame && pathSame && allParamsSame;

    return {
      ok: true,
      same,
      origin: { left: leftOrigin, right: rightOrigin, same: originSame },
      path: { left: l.pathname, right: r.pathname, same: pathSame },
      params,
      nested,
    };
  } catch (e) {
    return {
      ok: false,
      reason: 'unexpected error diffing URLs: ' + (e && e.message ? e.message : String(e)),
      same: false,
      origin: { left: String(leftUrl), right: String(rightUrl), same: false },
      path: { left: undefined, right: undefined, same: false },
      params: [],
      nested: {},
    };
  }
}

export default urlDiff;
